import { Box, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';

/** Props for {@link MobileActionBar}. */
export interface MobileActionBarProps {
  /**
   * Optional leading summary block, shown to the left of the action(s) — e.g. a
   * running total ("Cash out · $42.00") or a line/qty count. Kept compact so the
   * primary action never gets pushed off-screen.
   */
  summary?: ReactNode;
  /**
   * Optional short label rendered dim above {@link summary} (e.g. "Total",
   * "Cash delta"). Ignored when `summary` is omitted.
   */
  summaryLabel?: ReactNode;
  /**
   * The primary action(s) — typically the submit `<Button>`. Rendered on the
   * right, sized to fit; pass a single full-intent button for best results.
   */
  children: ReactNode;
}

/**
 * MobileActionBar — an IN-FLOW bottom action block for phone-width form
 * flows (Phase 3C-2 intake forms). Renders the running total + primary
 * submit as the last block of the form, in normal document flow.
 *
 * **Corrected 2026-08-25.** Extends R-TR5 (`docs/redesign-rulings.md`) from
 * the trade page to every intake flow. Product-owner ruling, verbatim: "yes
 * we should do them for buy and sale as well and legacy intake, the pinned
 * blocks the mobile bottom menu." This component used to render
 * `position: fixed; bottom: 0; z-index: 200` — sitting on top of
 * `PhoneTabBar` (`shells/desktop/PhoneTabBar.tsx`), which is deliberately
 * docked in Mantine's `AppShell.Footer` rather than fixed
 * (`DesktopLayout.tsx`) precisely so app chrome never covers content. A
 * `position: fixed` sibling at a higher z-index defeated that. It no longer
 * pins to the viewport at all, on any intake page.
 *
 * Shell: (desktop) management pages, phone breakpoint only. Render it as the
 * LAST sibling of the form, gated on `useIsNarrow()` (width only), so it lands after the
 * form body rather than mid-page. Call sites no longer need compensating
 * bottom padding on their scroll container — that padding existed only to
 * clear the fixed bar and has been removed at every call site.
 *
 * Anatomy:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ Label                          [ Primary CTA ] │
 *   │ Summary                                        │
 *   └──────────────────────────────────────────────┘
 *
 * Kept from the pinned version: the bordered container and the top divider
 * — it still visually sets the action row off from the form content above
 * it, in flow or not. The design system's own `CommitBar`
 * (`mtb-design-system/templates/vendor-txn/data.js`) keeps the identical
 * top border in its own `sticky: false` mode, so this isn't a local
 * invention. Dropped: the upward-cast `boxShadow` (it existed to lift a
 * pinned bar off scrolling content beneath it; in flow there is nothing
 * beneath it to lift off of, and it would read as stray elevation on an
 * ordinary block — `CommitBar`'s static mode carries no shadow either) and
 * the `env(safe-area-inset-bottom)` bottom padding (that inset only matters
 * flush against the physical screen edge, which this block no longer is).
 *
 * `summaryLabel` renders as an MTB eyebrow — mono, uppercase, `0.18em`
 * tracking — rather than the ad-hoc 10px/0.3px sans it used before #107.
 */
export function MobileActionBar({ summary, summaryLabel, children }: MobileActionBarProps) {
  return (
    <Box
      style={{
        background: 'var(--mantine-color-body)',
        borderTop: '1px solid var(--mantine-color-default-border)',
        padding: 'var(--space-2) var(--space-4)',
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
        {summary != null ? (
          <Stack gap={0} style={{ minWidth: 0, flexShrink: 1 }}>
            {summaryLabel != null && (
              <Text
                c="dimmed"
                tt="uppercase"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  letterSpacing: 'var(--tracking-eyebrow)',
                  fontWeight: 'var(--weight-semibold)',
                }}
              >
                {summaryLabel}
              </Text>
            )}
            <Box style={{ minWidth: 0 }}>{summary}</Box>
          </Stack>
        ) : (
          <span />
        )}
        <Box style={{ flexShrink: 0 }}>{children}</Box>
      </Group>
    </Box>
  );
}
